import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { DataTransferService } from '../../../../service/data-transfer.service';
import { GigsumoService } from '../../../../service/gigsumo.service';
import { JobProvider } from '../job-card/job-card.component';
import { getJobParams } from '../job-listing/job-listing.component';

/**
 * NEW DESIGN — right-hand sidebar of the /jobs detail page.
 * Reads the selected job from the shared JobProvider singleton (same pattern
 * as JobDetailsComponent). The Similar Jobs widget is wired to
 * `/jobs/findSimilarJobsForOpenSession`: it resolves the selected job id from
 * the route slug and fetches up to 4 title-related, publicly-visible jobs.
 * `applicantsCount` is wired live.
 */
@Component({
  selector: 'app-job-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './job-sidebar.component.html',
  styleUrl: './job-sidebar.component.scss'
})
export class JobSidebarComponent implements OnInit, OnDestroy {

  /** Up to 4 jobs whose title is similar to the selected job. */
  similarJobs: any[] = [];
  /** True while the similar-jobs request is in flight (drives the loading row). */
  loadingSimilar = false;
  /** True once a fetch has completed — gates the "couldn't find" empty message. */
  hasSearched = false;

  /** Avatar tints for the similar-job rows (cycled by index), matching the design. */
  private readonly avatarPalette = ['#2563EB', '#059669', '#D97706', '#7C3AED', '#DB2777'];

  /** Job id the widget last fetched for — guards against duplicate fetches. */
  private currentJid: string | null = null;
  private routeSub?: Subscription;
  private similarSub?: Subscription;

  constructor(
    public jobData: JobProvider,
    private gigsumoService: GigsumoService,
    private dataTransfer: DataTransferService,
    private route: ActivatedRoute,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnInit(): void {
    // The sidebar shares the listing's route, whose `jobId` param carries the
    // full SEO slug. Re-fetch similar jobs whenever the selected job changes.
    this.routeSub = this.route.paramMap.subscribe(params => {
      const jid = this.gigsumoService.extractJobId(params.get('jobId'));
      if (!jid || jid === this.currentJid) {
        return;
      }
      this.currentJid = jid;
      // The job the user came FROM - persisted on the JobProvider singleton so it
      // survives this component being recreated on navigation. Exclude it so it
      // doesn't reappear in the new job's widget.
      const cameFrom = (this.jobData.previousJobId && this.jobData.previousJobId !== jid)
        ? this.jobData.previousJobId : null;
      this.jobData.previousJobId = jid;
      this.fetchSimilarJobs(jid, cameFrom);
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.similarSub?.unsubscribe();
  }

  /** Fetch up to 4 similar jobs for the given job id (title-driven match).
   *  `excludeJobId` (optional) is the job the user came from, kept out of the list. */
  private fetchSimilarJobs(jobId: string, excludeJobId?: string | null): void {
    // Clear stale results up front so the previous job's list isn't shown while
    // the new request is in flight (otherwise it lingers until the response lands).
    this.similarJobs = [];
    this.loadingSimilar = true;
    this.similarSub?.unsubscribe();
    const body: any = { jobId };
    if (excludeJobId) {
      body.jobsToFilter = excludeJobId;   // also exclude the job the user navigated from
    }
    this.similarSub = this.dataTransfer
      .postData<any>('jobs/findSimilarJobsForOpenSession', body, 'similar-jobs')
      .subscribe({
        next: res => {
          this.similarJobs = res?.data?.jobList || [];
          this.loadingSimilar = false;
          this.hasSearched = true;
        },
        error: () => {
          this.similarJobs = [];
          this.loadingSimilar = false;
          this.hasSearched = true;
        }
      });
  }

  /** SEO slug for a similar job's detail route. */
  similarRoute(job: any): string {
    return this.gigsumoService.buildJobSlug(job);
  }

  /** Two-letter avatar initials from the job title. */
  similarInitials(job: any): string {
    return this.gigsumoService.getInitials(job?.jobTitle || '');
  }

  /** Avatar tint for the row at the given index. */
  similarColor(index: number): string {
    return this.avatarPalette[index % this.avatarPalette.length];
  }

  /** Sub-line: location (city, state) when available, otherwise the work mode +
   *  engagement type (jobClassification); the pay rate is always appended.
   *  e.g. "Washington, DC · $90–$110/hr" or "Remote · W2 - Contract · $90–$110/hr". */
  similarMeta(job: any): string {
    const location = [job?.city, job?.state].filter(Boolean).join(', ');
    const place = location || [job?.workMode, job?.jobClassification].filter(Boolean).join(' · ');
    return [place, this.similarRate(job)].filter(Boolean).join(' · ');
  }

  /** Compact rate, e.g. "$90–$110/hr" (no currency), matching the apply card. */
  private similarRate(job: any): string {
    const fmt = (v: any) => (v !== null && v !== undefined && `${v}` !== '' ? `$${v}` : '');
    const f = fmt(job?.targetRateFrom);
    const t = fmt(job?.targetRateTo);
    const amount = (f && t) ? `${f}–${t}` : (f || t);
    if (!amount) return '';
    const period = this.gigsumoService.getRatePeriod(job?.payType);
    return `${amount}${period ? '/' + period : ''}`;
  }

  /** Pre-seed the provider so the clicked job renders instantly on navigation. */
  onSelectSimilar(job: any): void {
    // Force the Job Details API (findJobsForOpenSession) to re-run for this job,
    // even if it's already in the listing cache - so a widget click behaves like
    // Global Search (both APIs fire for the new jobId).
    this.jobData.forceListingRefresh = true;
    this.jobData.setData(job);
  }

  // These MUST be getters (not precomputed): the sidebar is a singleton reused
  // across job selections, so stored values would freeze on the first job.

  /** Big rate, e.g. "$75 – $95" (no currency/period). */
  get rateAmount(): string {
    const job = this.jobData.getJob || {};
    const fmt = (v: any) => (v !== null && v !== undefined && `${v}` !== '' ? `$${v}` : '');
    const f = fmt(job.targetRateFrom);
    const t = fmt(job.targetRateTo);
    return (f && t) ? `${f} – ${t}` : (f || t);
  }

  /** Period suffix, e.g. "/hr". */
  get ratePeriodLabel(): string {
    const period = this.gigsumoService.getRatePeriod(this.jobData.getJob?.payType);
    return period ? `/${period}` : '';
  }

  /** Sub-line: "W2 – Contract · Hybrid · New York, NY". */
  get subLine(): string {
    const job = this.jobData.getJob || {};
    return [job.jobClassification, job.workMode, this.locationText()].filter(Boolean).join(' · ');
  }

  /** Company card sub-line — wireframe format: "<org type> · <city, state>".
   *  Org type comes from the user's `clientType` (e.g. "Staffing Agency",
   *  "Direct Client"). Backend has no dedicated `organisationType` field, so
   *  `clientType` is the closest match per the latest design call. */
  get companySub(): string {
    return [this.getClientType(), this.getCompanyLocation()].filter(Boolean).join(' · ');
  }

  /** Reads the user's clientType, falling back to the current-org work-
   *  experience entry if it isn't carried at the user root. */
  getClientType(): string {
    const user = this.jobData.getJob?.user;
    if (user?.clientType) return user.clientType;
    const exp = user?.workExperience;
    if (Array.isArray(exp)) {
      const current = exp.find((e: any) => e.currentOrganization === true);
      if (current?.clientType) return current.clientType;
    }
    return '';
  }

  /** Recruiter job-title line, e.g. "Senior Talent Acquisition Specialist".
   *  Reads the user's actual designation from their current-organization
   *  work-experience entry — same field the Members directory uses
   *  (workExperience[0].title). Falls back through any earlier work-experience
   *  entry, then user-root `title`/`designation`, and finally `user.userType`
   *  (converted from the backend's all-caps/underscored enum, e.g.
   *  `MANAGEMENT_TALENT_ACQUISITION` → `Management Talent Acquisition`). */
  get recruiterTitle(): string {
    const user = this.jobData.getJob?.user;
    if (!user) return '';
    const exp = user.workExperience;
    if (Array.isArray(exp)) {
      const current = exp.find((e: any) => e.currentOrganization === true);
      if (current?.title) return current.title;
      if (current?.designation) return current.designation;
      const any = exp.find((e: any) => e?.title || e?.designation);
      if (any) return any.title || any.designation;
    }
    if (user.title) return user.title;
    if (user.designation) return user.designation;
    return this.formatUserType(user.userType);
  }

  /** Convert a backend user-type enum (e.g. `MANAGEMENT_TALENT_ACQUISITION`)
   *  into a display string (`Management Talent Acquisition`). Splits on
   *  underscores, lowercases everything, then upper-cases the first letter
   *  of each word. */
  private formatUserType(userType: any): string {
    if (!userType || typeof userType !== 'string') return '';
    return userType
      .split('_')
      .filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  private locationText(): string {
    const job = this.jobData.getJob || {};
    return [job.city, job.state].filter(Boolean).join(', ');
  }

  getCompanyInitials(): string {
    return this.gigsumoService.getInitials(this.jobData.getJob?.jobPostedBehalfOf || '');
  }

  getRecruiterName(): string {
    const job = this.jobData.getJob || {};
    // Backend returns `user.fullname` (lowercase n) — see backend-api-todos.md.
    return job.postedByUserName || job.user?.fullname || '';
  }

  getRecruiterInitials(): string {
    return this.gigsumoService.getInitials(this.getRecruiterName());
  }

  // First name only — used in the platform pitch ("connect with {first} and …").
  getRecruiterFirstName(): string {
    const full = this.getRecruiterName();
    return full ? full.trim().split(/\s+/)[0] : '';
  }

  getCompanyLocation(): string {
    const exp = this.jobData.getJob?.user?.workExperience;
    if (Array.isArray(exp)) {
      const current = exp.find((e: any) => e.currentOrganization === true);
      if (current) {
        return [current.city, current.state].filter(Boolean).join(', ');
      }
    }
    return '';
  }

  getOrgTypeLabel(): string {
    const map: { [k: string]: string } = {
      RECRUITER: 'Recruiter',
      FREELANCE_RECRUITER: 'Freelance Recruiter',
      BENCH_RECRUITER: 'Bench Sales',
      JOB_SEEKER: 'Job Seeker',
      MANAGEMENT_TALENT_ACQUISITION: 'Management Talent Acquisition',
    };
    const type = this.jobData.getJob?.jobPostedByUserType;
    return (type && map[type]) || '';
  }

  getLoginParams(job: any): string {
    return getJobParams(job);
  }

  onShare(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const url = window.location.href;
    const title = this.jobData.getJob?.jobTitle || 'Job';
    const nav: any = window.navigator;
    if (nav?.share) {
      nav.share({ title, url }).catch(() => { /* user cancelled */ });
    } else if (nav?.clipboard) {
      nav.clipboard.writeText(url);
    }
  }

  onSave(): void {
    // Deferred: full save needs an authenticated session.
  }
}
